import { useQuery } from '@tanstack/react-query';

import type { SessionStatus } from '@heidi/schema/ml-scribe';

import { GET_SESSION_STATUS_COUNTS } from '../../contracts';
import { getSessionStatusCountsV2 } from '../api/list';
import { getSessionDataConfig } from '../config';
import { SESSIONS_REFETCH_INTERVAL } from './sessionsList';

/**
 * All Sessions (`scope=all`) status counts, backing the status-filter
 * dropdown's count badges — one whole-scope call shared by every status row
 * (own+team de-duped, direct shares excluded). The query key carries no filter
 * params, so it never refetches on date/search/patient/clinician/urgency
 * changes.
 *
 * `enabled` arrives resolved because every input to it is app state the package
 * may not read: the session-list scope is a UI atom, the rollout flag is app
 * platform, and "the user has loaded" is a workspace query. The auth half is
 * NOT passed in — it comes through the injected `useAuthToken` port, so a token
 * refresh re-gates this query the same way it re-gates every other session
 * read.
 */
export function useQueryAllSessionsStatusCounts({
  status,
  enabled,
}: {
  status: SessionStatus;
  /**
   * The non-auth half of the gate, resolved by the caller: the user has
   * loaded, the list scope is `all`, and the rollout flag is on.
   *
   * The `scope === all` part is load-shedding only — it stops the whole-scope
   * endpoint firing on other scopes. The display decision (which count to show
   * per scope) is owned by the caller too; both must move together if a future
   * scope also needs whole-scope counts.
   */
  enabled: boolean;
}) {
  const token = getSessionDataConfig().useAuthToken?.();

  return useQuery({
    queryKey: [GET_SESSION_STATUS_COUNTS],
    queryFn: async () => {
      const { data } = await getSessionStatusCountsV2();
      return data;
    },
    select: (data) => data.counts[status],
    enabled: Boolean(token) && enabled,
    staleTime: SESSIONS_REFETCH_INTERVAL,
  });
}
